import React, { useEffect, useMemo, useState } from 'react'
import SimpleBar from 'simplebar-react'
import { XClose, Minus, Plus } from '@untitled-ui/icons-react'
import PaymentMethodPicker from '../PaymentMethodPicker'
import { BillingService } from '../../billing.service'
import { useCompany } from '../../Context'
import { formatIDR } from '../../billing.format'

// Topup quota — slide-in side panel, opened beside the company DetailSlider.
// Pricing is driven by the company's ACTIVE subscription per-enquiry rate.
// Mirrors the NewEnquirySlider stack pattern (plain <div>, no portal).

function TopupPanel({ companyId, onClose }) {
  const { openInvoicePanel } = useCompany()
  const [subscription, setSubscription] = useState(null)
  const [enquiryCount, setEnquiryCount] = useState(50)
  const [paymentMethod, setPaymentMethod] = useState(null)
  const [methodFeeIdr, setMethodFeeIdr] = useState(0)
  // Which amount that fee answers — the picker refetch is debounced, so right
  // after qty/plan changes the fee on screen belongs to the previous amount.
  const [feePricedFor, setFeePricedFor] = useState(null)
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!companyId) return
    setLoading(true)
    setError(null)
    BillingService.getSubscription(companyId)
      .then((res) => {
        const sub = res?.data ?? res
        if (!sub || sub.status !== 'active') {
          setError('No active subscription. Subscribe to a plan before topping up.')
        }
        setSubscription(sub || null)
      })
      .catch((err) =>
        setError(err?.response?.data?.message || err.message || 'Failed to load subscription'),
      )
      .finally(() => setLoading(false))
  }, [companyId])

  const plan = subscription?.pricing_plan

  const summary = useMemo(() => {
    if (!plan) return null
    const perItem = Number(plan.per_enquiry_fee_idr || 0)
    const qty = Number(enquiryCount || 0)
    const subtotal = Number((perItem * qty).toFixed(2))
    // Tax temporarily zeroed by business (2026-06-01); keep row for clarity.
    const tax = 0
    // Priced by the server for this exact method and amount. The portal used to
    // keep its own copy of the fee table, which could not follow an operator's
    // edits and could not express a percentage tariff at all.
    const appFee = Number(methodFeeIdr || 0)
    const total = Number((subtotal + appFee + tax).toFixed(2))
    return { qty, perItem, subtotal, appFee, tax, total }
  }, [plan, enquiryCount, methodFeeIdr])

  // Guard against absurd / typo top-ups (this mints a real invoice, pay-per-unit).
  const MAX_ENQUIRY_TOPUP = 10000

  const adjustQty = (delta) => {
    setEnquiryCount((cur) => {
      const next = Math.min(MAX_ENQUIRY_TOPUP, Math.max(1, Number(cur || 0) + delta))
      return next
    })
  }

  const submit = async () => {
    if (!subscription || subscription.status !== 'active' || !plan) return
    if (!Number.isInteger(enquiryCount) || enquiryCount < 1) {
      setError('Enquiry count must be at least 1')
      return
    }
    if (enquiryCount > MAX_ENQUIRY_TOPUP) {
      setError(`Enquiry count must not exceed ${MAX_ENQUIRY_TOPUP.toLocaleString('id-ID')}`)
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const res = await BillingService.topup(companyId, {
        enquiry_count: enquiryCount,
        payment_methods: [paymentMethod],
      })
      // Open the in-app invoice detail panel instead of redirecting to the
      // Xendit hosted page. The panel shows VA number / payment link and a
      // "Pay now" button, so the user stays inside the backoffice.
      const invoiceId = res?.data?.invoice?.id ?? res?.invoice?.id
      if (!invoiceId) throw new Error('No invoice returned')
      openInvoicePanel(invoiceId)
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to create topup invoice')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="flex h-screen w-[420px] flex-col bg-white shadow-xl">
      <header className="relative flex items-start gap-x-4 border-b border-gray-100 px-6 py-6">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-[12px] top-[12px] flex h-10 w-10 items-center justify-center rounded-lg p-2 text-gray-400 hover:bg-gray-50 active:bg-gray-100"
        >
          <XClose size={20} stroke="currentColor" />
        </button>
        <div className="flex flex-1 flex-col gap-1 pt-1">
          <p className="text-lg font-semibold text-gray-900">Top up quota</p>
          <p className="text-sm font-medium text-gray-500">
            {plan
              ? `${plan.name} — ${formatIDR(plan.per_enquiry_fee_idr)} per enquiry`
              : 'Buy more enquiry quota'}
          </p>
        </div>
      </header>

      <section className="flex-1 overflow-hidden">
        <SimpleBar forceVisible="y" style={{ maxHeight: '100%' }}>
          <div className="flex flex-col gap-6 px-6 py-6">
            {error ? (
              <div className="rounded-md border border-error/200 bg-error/50 p-3 text-sm text-error/700">
                {error}
              </div>
            ) : null}

            {/* ── Quantity input (mirror Figma "Purchase more quota") ── */}
            <div className="rounded-xl border border-gray-light/200 bg-white p-5 shadow-shadows/shadow-xs">
              <p className="text-sm font-semibold text-gray-900">Input enquiries you want to buy</p>
              <div className="mt-3 flex items-stretch gap-2">
                <button
                  type="button"
                  onClick={() => adjustQty(-10)}
                  className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-gray-light/300 text-gray-light/700 hover:bg-gray-light/50"
                  aria-label="Decrease 10"
                >
                  <Minus size={18} />
                </button>
                <input
                  type="number"
                  min={1}
                  max={MAX_ENQUIRY_TOPUP}
                  value={enquiryCount}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10)
                    setEnquiryCount(Number.isNaN(v) ? 0 : Math.min(MAX_ENQUIRY_TOPUP, Math.max(0, v)))
                  }}
                  className="h-12 w-full min-w-0 flex-1 rounded-lg border border-brand/600 bg-white px-3 text-center text-2xl font-semibold text-gray-900 outline-none focus:ring-2 focus:ring-brand/200"
                />
                <button
                  type="button"
                  onClick={() => adjustQty(10)}
                  className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-gray-light/300 text-gray-light/700 hover:bg-gray-light/50"
                  aria-label="Increase 10"
                >
                  <Plus size={18} />
                </button>
              </div>
              <div className="mt-4 border-t border-gray-light/200 pt-3 text-center">
                <p className="text-xs text-gray-light/600">Total amount to be paid</p>
                <p className="mt-1 text-2xl font-bold text-gray-900">
                  {summary ? formatIDR(summary.total) : '—'}
                </p>
              </div>
            </div>

            {/* ── Order summary breakdown ── */}
            {summary ? (
              <div className="overflow-hidden rounded-xl border border-gray-light/200 bg-gray-light/25 shadow-shadows/shadow-xs">
                <div className="px-5 pb-2 pt-3">
                  <p className="text-sm font-semibold text-gray-900">Order summary</p>
                </div>
                <div className="rounded-xl border border-gray-light/200 bg-white shadow-shadows/shadow-xs">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-t border-gray-light/200 bg-white">
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500">
                          Items
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500">
                          Qty
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500">
                          Amount
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="px-6 py-4 text-sm text-gray-600">Enquiry</td>
                        <td className="px-6 py-4 text-right text-sm text-gray-600">
                          x{summary.qty.toLocaleString('id-ID')}
                        </td>
                        <td className="px-6 py-4 text-right text-sm text-gray-600">
                          {formatIDR(summary.perItem)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="px-5 pb-3 pt-2">
                  <div className="flex flex-col">
                    <div className="flex items-center py-3">
                      <span className="flex-1 text-sm text-gray-600">Subtotal</span>
                      <span className="text-sm text-gray-600">{formatIDR(summary.subtotal)}</span>
                    </div>
                    <div className="flex items-center py-3">
                      <span className="flex-1 text-sm text-gray-600">App fee</span>
                      <span className="text-sm text-gray-600">{formatIDR(summary.appFee)}</span>
                    </div>
                    <div className="flex items-center py-3">
                      <span className="flex-1 text-sm text-gray-600">Tax</span>
                      <span className="text-sm text-gray-600">{formatIDR(summary.tax)}</span>
                    </div>
                    <div className="flex items-center border-t border-gray-light/200 py-3">
                      <span className="flex-1 text-sm font-medium text-gray-900">Total</span>
                      <span className="text-sm font-medium text-gray-900">
                        {formatIDR(summary.total)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            {/* ── Payment method ── */}
            <div className="overflow-hidden rounded-xl border border-gray-light/200 bg-gray-light/25 shadow-shadows/shadow-xs">
              <div className="px-5 pb-2 pt-3">
                <p className="text-sm font-semibold text-gray-900">Payment method</p>
              </div>
              <div className="rounded-xl border border-gray-light/200 bg-white px-6 py-3 shadow-shadows/shadow-xs">
                <PaymentMethodPicker
                  companyId={companyId}
                  amountIdr={summary ? Math.round(summary.subtotal) : null}
                  value={paymentMethod}
                  onChange={(wire, m) => {
                    setPaymentMethod(wire)
                    setMethodFeeIdr(Number(m?.app_fee_idr || 0))
                    setFeePricedFor(m ? m.priced_for_idr ?? null : null)
                  }}
                />
              </div>
            </div>
          </div>
        </SimpleBar>
      </section>

      <div className="border-t border-gray-light/200 px-6 py-6">
        <button
          type="button"
          onClick={submit}
          disabled={
            loading ||
            submitting ||
            !subscription ||
            subscription.status !== 'active' ||
            !Number.isInteger(enquiryCount) ||
            enquiryCount < 1 ||
            !paymentMethod ||
            // fee di summary harus jawaban utk nominal INI, bukan sisa debounce
            feePricedFor !== (summary ? Math.round(summary.subtotal) : null)
          }
          className="w-full rounded-lg bg-brand/600 px-4 py-2.5 text-base font-semibold text-white shadow-shadows/shadow-xs hover:bg-brand/700 disabled:opacity-50"
        >
          {submitting ? 'Processing…' : 'Continue to payment'}
        </button>
      </div>
    </div>
  )
}

export default TopupPanel
